import Grid from "@ls/react-grid";
import { memo, useCallback, useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  selectBandedRows,
  selectGridFontSize,
  selectGridHeaderHeight,
  selectGridRowHeight,
  selectGridVerticalLines,
  selectHideGridSideBar,
  selectDefaultExpansion,
} from "state/app/selectors";
import { useColumnDefs, useGridInstance } from "state/grid";
import { selectRows } from "state/grid/selector";
import SelectionState from "state/selections/SelectionState";
import {
  selectSelectedAccountEntity,
  selectSelectedAccountEntityId,
  selectSelectedGroupingLevels,
  selectSelectedIsLookthru,
  selectSelectedAsOfDate,
  selectSecurityLabel,
} from "state/selections/selectors";
import styled from "styled-components";
import GroupCellRenderer from "./GroupCellRenderer";
import { useGridStateModule as useGridStateModuleAzure } from "@ls/grid-state-module-azure";
import { useGridStateModule as useGridStateModuleLegacy } from "@ls/grid-state-module";

const getDataPath = (data) => data.path;
const getRowStyle = (args) => {
  const style = {
    cursor: "pointer",
  };
  const { data } = args;
  if (data?.bucket === "ROOT") {
    style.background = "#ced5e4";
    style.fontWeight = "700";
  }
  return style;
};
const defaultColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  suppressMenu: true,
  floatingFilter: true,
};
const gridFrameworkComponents = {
  GroupCellRenderer,
};

const Root = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const getRowNodeId = (args) => args.pathString;

const ResultGrid = (props) => {
  const rowData = useSelector(selectRows);
  const columnDefs = useColumnDefs();
  const [grid, setGrid] = useGridInstance();
  const selectedAccountEntityId = useSelector(
    selectSelectedAccountEntityId(SelectionState.COMMITTED)
  );
  const selectedIsLookThru = useSelector(
    selectSelectedIsLookthru(SelectionState.COMMITTED)
  );
  const selectedGroupingLevels = useSelector(
    selectSelectedGroupingLevels(SelectionState.COMMITTED)
  );
  const selectedAsOfDate = useSelector(
    selectSelectedAsOfDate(SelectionState.COMMITTED)
  );
  const hideGridSideBar = useSelector(selectHideGridSideBar);
  const headerHeight = useSelector(selectGridHeaderHeight);
  const defaultExpansion = useSelector(selectDefaultExpansion);
  const accountEntity = useSelector(
    selectSelectedAccountEntity(SelectionState.COMMITTED)
  );
  const [sectorViewGuId, setSectorViewGuId] = useState(null);
  const securityLabel = useSelector(
    selectSecurityLabel(SelectionState.COMMITTED)
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: "Bucket Levels",
      X_formatType: "TEXT",
      suppressMenu: true,
      enablePivot: false,
      enableRowGroup: false,
      pinned: "left",
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: "GroupCellRenderer",
      },
      width: 450,
      rowDrag: false,
      filter: "agTextColumnFilter",
      suppressSizeToFit: true,
      suppressMovable: true,
      cellStyle: {
        backgroundColor: "unset",
        paddingRight: 0,
      },
      valueGetter: (args) => {
        const { data } = args;
        if (data.securityRow) {
          if (securityLabel) {
            return `${
              data.securityAttributes?.[securityLabel] ?? "[Missing Attribute]"
            }`;
          } else {
            return data.securityAttributes?.defaultSecurityId
              ? `${data.securityAttributes.name}##${data.securityAttributes.defaultSecurityId}`
              : data.securityAttributes?.name ?? data.securityId;
          }
        } else if (data.bucket === "ROOT") {
          return "Total";
        } else {
          return data.bucket ?? "N/A";
        }
      },
    }),
    [securityLabel]
  );

  /**
   * in2AppTalk message listener for Sector View window ID
   */
  useEffect(() => {
    const { in2AppTalk } = window;
    if (!in2AppTalk) {
      return;
    }
    const instance = in2AppTalk.getInstance();
    const listener = (sub, msg) => {
      if (typeof msg.containsKey === "function") {
        if (msg.containsKey("SECTOR_VIEW_GUID")) {
          setSectorViewGuId(msg.get("SECTOR_VIEW_GUID"));
        }
      }
    };
    instance.subscribe("*", listener);
    return () => {
      if (in2AppTalk) {
        instance.unsubscribe("*", listener);
      }
    };
  }, []);

  const gridStateModuleAzure = useGridStateModuleAzure();
  const gridStateModuleLegacy = useGridStateModuleLegacy();

  const gridStateModule =
    process.env.REACT_APP_HOST_ENV === "AZURE"
      ? gridStateModuleAzure
      : gridStateModuleLegacy;

  const onGridReady = useCallback(
    (grid) => {
      setGrid(grid);
    },
    [setGrid]
  );

  const generateSecurityIdList = useCallback((row, repSecIds = []) => {
    if (row.groupingNodes.length !== 0) {
      // traverse until we arrive at the final grouping level
      row.groupingNodes.forEach((node) => {
        return generateSecurityIdList(node, repSecIds);
      });
    } else {
      row.entityReferences.forEach((entity) =>
        repSecIds.push(entity.securityId)
      );
    }
    return repSecIds;
  }, []);

  const sendSectorViewMessage = useCallback(
    (params) => {
      const {
        node: { data: row },
      } = params;
      const { in2AppTalk } = window;
      if (!in2AppTalk) {
        console.error("in2AppTalk not available.");
        return;
      }
      const msg = new window.HashMap();
      const levelValues = row.bucketPath?.slice(1); //first bucketPath element is always ROOT. listener does not want this
      if (row.securityRow) {
        levelValues.pop(); //last element is securityID. listener does not want this.
      }
      const { type } = accountEntity ?? {};
      let groupType;
      if (type === "COMP") {
        groupType = "composite";
      } else if (type === "ACCOUNT" || type === "BMARK") {
        groupType = "entity_list";
      } else {
        groupType = type;
      }
      const msgBody = {
        IDENTIFIERS: [selectedAccountEntityId],
        GROUP_TYPE: groupType,
        lookthru: selectedIsLookThru,
        asOfDate: selectedAsOfDate,
        LEVEL_BUCKETS: selectedGroupingLevels,
        LEVEL_VALUES: levelValues,
        NAME_LABEL: row.securityRow ? row.securityAttributes.name : row.bucket,
        CUSIP: row.securityRow ? row.securityAttributes.cusip : null,
        REP_SEC_IDS: row.securityRow
          ? [row.securityId]
          : generateSecurityIdList(row),
      };
      console.log("sendSectorViewMessage", msgBody);
      msg.put(
        "SECTOR_VIEW_MESSAGE",
        window.encodeURIComponent(JSON.stringify(msgBody))
      );
      in2AppTalk
        .getInstance()
        .publish(in2AppTalk.MSG_TYPE.PRIMARY_STACKED_ITEM_CHANGE, msg);
      if (sectorViewGuId) {
        window.in2.Focus(sectorViewGuId);
      }
    },
    [
      accountEntity,
      selectedAccountEntityId,
      selectedAsOfDate,
      selectedGroupingLevels,
      selectedIsLookThru,
      generateSecurityIdList,
      sectorViewGuId,
    ]
  );

  const getContextMenuItems = useCallback(
    (params) => {
      return [
        "copy",
        "copyWithHeaders",
        {
          name: "Open Sector View",
          action: () => sendSectorViewMessage(params),
        },
      ];
    },
    [sendSectorViewMessage]
  );

  useEffect(() => {
    /**
     * ag-grid does not update with a new getContextMenuItems callback.
     * This is a workaround for that.
     * This will probably be fixed in a future version of ag-grid.
     */
    if (grid) {
      grid.api.gridOptionsWrapper.setProperty(
        "getContextMenuItems",
        getContextMenuItems
      );
      grid?.api?.forEachNodeAfterFilter((node, b) => {
        if (
          node.level < defaultExpansion - 1 &&
          !node.expanded &&
          node.isExpandable()
        ) {
          node.expanded = true;
        } else if (
          node.level >= defaultExpansion - 1 &&
          node.expanded &&
          node.isExpandable()
        ) {
          node.expanded = false;
        }
      });
      grid.api.onGroupExpandedOrCollapsed();
    }
  }, [grid, getContextMenuItems, defaultExpansion]);
  const bandedRows = useSelector(selectBandedRows);
  const enableVerticalGridLines = useSelector(selectGridVerticalLines);
  const fontSize = useSelector(selectGridFontSize);
  const rowHeight = useSelector(selectGridRowHeight);

  const onRowSelected = useCallback(
    (arg) => {
      if (!arg.node.selected) {
        //this fires when node is unselected. we dont care about those.
        return;
      }
      const { in2AppTalk, NameValuePair } = window;
      if (!in2AppTalk) {
        console.error("in2AppTalk not available.");
        return;
      }

      const selectedRows = grid?.api?.getSelectedRows() ?? [];
      if (selectedRows.length === 1) {
        const row = selectedRows[0];
        const sendOwnershipMessageV2 = () => {
          /**
         *
         * OWNERSHIP_TREE_MSG:  OWNERSHIP_TREE_MSG
         IDENTIFIERS: <entity list, group id, platform id, etc>
         GROUP_TYPE: <entity_list | custom | product | etc>
         (perhaps if you know the distinct set of entityIds for the positions represented at the clicked level and below in the tree
         then the most efficient thing would be for you to send over an entity_list type and only those entity ids for the positions)

         lookthru: true|false

         LEVEL_BUCKETS: FD_CUSTOM_1,FD_CUSTOM_2,FD_CUSTOM_3....
         LEVEL_VALUES: <level_1_value>@@@@<level_2_value>@@@@<level_3_value>....

         if leaf level clicked add this key/value as well
         REP_SEC_ID: <repsecid>
         */
          const msg = new window.HashMap();
          let levelValues = row.bucketPath?.slice(1); //first bucketPath element is always ROOT. listener does not want this
          if (row.securityRow) {
            levelValues.pop(); //last element is securityID. listener does not want this.
          }
          const { type } = accountEntity ?? {};
          let groupType;
          if (type === "COMP") {
            groupType = "composite";
          } else if (type === "ACCOUNT" || type === "BMARK") {
            groupType = "entity_list";
          } else {
            groupType = type;
          }
          const msgBody = {
            IDENTIFIERS: [selectedAccountEntityId],
            GROUP_TYPE: groupType,
            lookthru: selectedIsLookThru,
            LEVEL_BUCKETS: levelValues.map(
              (val, idx) => selectedGroupingLevels[idx]
            ), //only levels up to where the user actually clicked
            LEVEL_VALUES: levelValues,
            REP_SEC_ID: row.securityRow ? row.securityId : null,
          };
          console.log("Sending OWNERSHIP_TREE_MSG", msgBody);
          msg.put(
            "OWNERSHIP_TREE_MSG",
            window.encodeURIComponent(JSON.stringify(msgBody))
          );
          in2AppTalk
            .getInstance()
            .publish(in2AppTalk.MSG_TYPE.PRIMARY_STACKED_ITEM_CHANGE, msg);
        };
        sendOwnershipMessageV2();

        if (row.securityRow) {
          const sendSecMessage = () => {
            const { securityId, securityAttributes } = row;
            const { cusip, gpsId } = securityAttributes ?? {};
            const msg = [];
            if (cusip?.length) {
              msg.push(
                new NameValuePair(
                  in2AppTalk.PS_MSG_TYPE.SECURITY_CUSIP_ID,
                  cusip
                )
              );
            }
            if (gpsId?.length) {
              msg.push(
                new NameValuePair(
                  in2AppTalk.PS_MSG_TYPE.SECURITY_GPS_SEC_ID,
                  gpsId
                )
              );
            }
            if (securityId) {
              msg.push(
                new NameValuePair(
                  in2AppTalk.PS_MSG_TYPE.SECURITY_FIDR_REPSEC_ID,
                  securityId
                )
              );
            }
            in2AppTalk
              .getInstance()
              .publish(in2AppTalk.MSG_TYPE.PRIMARY_LIST_CHANGE, msg);
          };

          const sendOwnershipMessage = () => {
            const msg = new window.HashMap();
            const { securityAttributes, bucketPath } = row;
            msg.put("GROUP_ACCOUNT_ENTITY_ID", String(selectedAccountEntityId));
            msg.put("VIEW_NAME", "MUNI_OBLIGOR,SECURITY_NAME");
            msg.put(
              "TREE_PATH",
              bucketPath
                .slice(bucketPath.length - 2, bucketPath.length)
                .join("@@@@")
            );
            msg.put(
              in2AppTalk.PS_MSG_TYPE.SECURITY_FIDR_REPSEC_ID,
              securityAttributes?.name
            );
            msg.put(
              in2AppTalk.PS_MSG_TYPE.SECURITY_NAME,
              securityAttributes?.name +
                "<BR> Cusip - " +
                String(securityAttributes?.cusip) +
                "<BR> ISIN - " +
                securityAttributes?.isin +
                "<BR>"
            );
            msg.put(
              in2AppTalk.PS_MSG_TYPE.CUSIP_ISIN,
              String(securityAttributes?.cusip) ?? ""
            );
            msg.put("lookthru", String(selectedIsLookThru));
            in2AppTalk
              .getInstance()
              .publish(in2AppTalk.MSG_TYPE.PRIMARY_STACKED_ITEM_CHANGE, msg);
          };

          if (window.in2) {
            sendSecMessage();
            sendOwnershipMessage();
          }
        }
      }
    },
    [
      accountEntity,
      grid,
      selectedAccountEntityId,
      selectedGroupingLevels,
      selectedIsLookThru,
    ]
  );

  if (process.env.REACT_APP_IN2_ENV !== "PRD") {
    console.log("rowData", rowData);
    console.log("columnDefs", columnDefs);
  }

  return (
    <Root>
      <Grid
        getDataPath={getDataPath}
        getRowStyle={getRowStyle}
        defaultColDef={defaultColDef}
        autoGroupColumnDef={autoGroupColumnDef}
        frameworkComponents={gridFrameworkComponents}
        gridStateModule={gridStateModule}
        bandedRows={bandedRows}
        enableVerticalGridLines={enableVerticalGridLines}
        fontSize={fontSize}
        rowHeight={rowHeight}
        headerHeight={headerHeight}
        onRowSelected={onRowSelected}
        immutableData
        getRowNodeId={getRowNodeId}
        X_hideSideBar={hideGridSideBar}
        onGridReady={onGridReady}
        rowData={rowData}
        columnDefs={columnDefs}
        suppressContextMenu={false}
        groupSelectsChildren={false}
        groupDefaultExpanded={defaultExpansion - 1}
        rowSelection="multiple"
        suppressFieldDotNotation
        excludeChildrenWhenTreeDataFiltering
        treeData
        suppressCellSelection
        enableRangeSelection
        enableActionButtons
        suppressCopyRowsToClipboard
        hidePdf={props.exlDisclaimer}
        X_exportURL={process.env.REACT_APP_EXPORT_SERVICE || null}
      />
    </Root>
  );
};

export default memo(ResultGrid);
